import { type Moment } from 'moment/moment';
import moment from 'moment';
import 'moment-timezone';
import { isSameDay } from 'react-dates';

export const addLeadingZeros = (dateString: string): string => {
  const parts = dateString.split('/');
  if (parts[0] && parts[0].length === 1) parts[0] = `0${parts[0]}`;
  if (parts[1] && parts[1].length === 1) parts[1] = `0${parts[1]}`;
  return parts.join('/');
};

export const formatYear = (dateString: string): string => {
  const parts = dateString.split('/');
  if (parts.length === 3 && parts[2].length === 2) {
    const currentYear = moment().year();
    const inputYear = parseInt(parts[2], 10);
    const currentCentury = Math.floor(currentYear / 100) * 100;
    const formattedYear = inputYear + currentCentury;
    if (formattedYear > currentYear) {
      parts[2] = (formattedYear - 100).toString();
    } else {
      parts[2] = formattedYear.toString();
    }
  }
  return parts.join('/');
};

export const formatDateInput = (value: string): string => {
  const cleanedValue = value.replace(/[\s\-/]+/g, '');

  if (cleanedValue.length === 6 || cleanedValue.length === 8) {
    const month = cleanedValue.slice(0, 2);
    const day = cleanedValue.slice(2, 4);
    const year = cleanedValue.slice(4);

    const formattedDate = `${month}/${day}/${year}`;
    return addLeadingZeros(formatYear(formattedDate));
  }

  return value;
};

const mtz = moment as unknown as typeof moment & {
  tz: {
    (input?: moment.MomentInput, timezone?: string): Moment;
    (input: moment.MomentInput, format: string, timezone: string): Moment;
    (
      input: {
        year: number;
        month: number;
        day: number;
        hour?: number;
        minute?: number;
        second?: number;
        millisecond?: number;
      },
      timezone: string
    ): Moment;
    zone(timezone: string): {
      name: string;
      abbrs: string[];
      untils: number[];
      offsets: number[];
      population: number;
    } | null;
  };
};

const TIME_FORMAT = 'HH:mm:ss';

export const isValidTimezone = (timezone: string): boolean => {
  try {
    return Boolean(mtz.tz?.zone?.(timezone));
  } catch (e) {
    return false;
  }
};

export const isSameDayInTz = (a: Moment, b: Moment, tz?: string) => {
  if (!tz) return isSameDay(a, b);
  const az = mtz.tz({ year: a.year(), month: a.month(), day: a.date() }, tz);
  const bz = mtz.tz({ year: b.year(), month: b.month(), day: b.date() }, tz);
  return az.isSame(bz, 'day');
};

export const buildTzInstant = (
  d: Moment,
  hhmmss: string,
  tz?: string
): Moment | null => {
  if (!tz) return null;
  const t = moment(hhmmss, TIME_FORMAT, true);
  if (!t.isValid()) return null;
  return mtz.tz(
    {
      year: d.year(),
      month: d.month(),
      day: d.date(),
      hour: t.hour(),
      minute: t.minute(),
      second: t.second(),
      millisecond: 0,
    },
    tz
  );
};

export const combineDateWithTime = (
  date: Moment,
  timeString: string
): Moment => {
  const time = moment(timeString || '', TIME_FORMAT, true);
  const base = date.clone().milliseconds(0);
  return time.isValid()
    ? base.hour(time.hour()).minute(time.minute()).second(time.second())
    : base;
};

export type DstCheck =
  | { kind: 'ok'; m: Moment }
  | { kind: 'gap'; transitionLocal?: string; message: string }
  | {
      kind: 'overlap';
      earlier: Moment;
      later: Moment;
      transitionLocal?: string;
      message: string;
    }
  | { kind: 'tz_error'; message: string };

function findNearestOffsetShift(
  around: Moment,
  direction: 'forward' | 'backward',
  hoursSpan = 3
): Moment | null {
  const start = around.clone().subtract(hoursSpan, 'hour');
  const end = around.clone().add(hoursSpan, 'hour');
  const cur = start.clone();
  let prev = cur.utcOffset();

  while (cur.isBefore(end)) {
    cur.add(1, 'minute');
    const off = cur.utcOffset();

    const isShift = direction === 'forward' ? off > prev : off < prev;
    if (isShift) return cur.clone();

    prev = off;
  }
  return null;
}

const findNearestForwardShift = (around: Moment, hoursSpan = 3) =>
  findNearestOffsetShift(around, 'forward', hoursSpan);

const findNearestBackwardShift = (around: Moment, hoursSpan = 3) =>
  findNearestOffsetShift(around, 'backward', hoursSpan);

export function checkDstWallTime(
  tz: string,
  date: Moment,
  timeStr: string
): DstCheck {
  if (!tz || !date || !timeStr || !date.isValid())
    return { kind: 'ok', m: date || moment() };

  if (!isValidTimezone(tz)) {
    return {
      kind: 'tz_error',
      message: `Unknown timezone: ${tz}. Please check timezone configuration.`,
    };
  }

  const wall = buildTzInstant(date, timeStr, tz);
  if (!wall) return { kind: 'ok', m: date };

  const t = moment(timeStr, 'HH:mm:ss', true);

  const same =
    wall.year() === date.year() &&
    wall.month() === date.month() &&
    wall.date() === date.date() &&
    wall.hour() === t.hour() &&
    wall.minute() === t.minute() &&
    wall.second() === t.second();

  if (!same) {
    const transition = findNearestForwardShift(wall.clone(), 3);

    let timeRangeText = '';
    if (transition) {
      const offBefore = transition.clone().subtract(1, 'minute').utcOffset();
      const offAfter = transition.clone().utcOffset();
      const gapMinutes = Math.abs(offAfter - offBefore);

      const gapEndLocal = transition.clone().tz(tz);

      const gapStartLocal = gapEndLocal
        .clone()
        .utcOffset(offBefore, true)
        .subtract(gapMinutes, 'minute');

      const startTime = gapStartLocal.format('h:mm A');
      const endTime = gapEndLocal.format('h:mm A');
      timeRangeText = ` ${startTime} – ${endTime}`;
    }

    return {
      kind: 'gap',
      transitionLocal:
        transition?.tz(tz).format('YYYY-MM-DD HH:mm') ?? undefined,
      message: `The selected time cannot be set accurately due to a Daylight Saving Time transition. Please choose a time outside the${timeRangeText} window for this date.`,
    };
  }

  const transition = findNearestBackwardShift(wall.clone(), 6);
  if (transition) {
    const offBefore = transition.clone().subtract(1, 'minute').utcOffset();
    const offAfter = transition.clone().add(1, 'minute').utcOffset();

    const startAmbigLocal = transition.clone().subtract(1, 'hour').tz(tz);
    const endAmbigLocal = transition.clone().tz(tz);

    const displayEndLocal = endAmbigLocal.clone().add(1, 'hour');

    const wallLocal = wall.clone().tz(tz);

    if (
      wallLocal.isSameOrAfter(startAmbigLocal) &&
      wallLocal.isBefore(endAmbigLocal)
    ) {
      const wallStr = wallLocal.format('YYYY-MM-DD HH:mm:ss');
      const earlier = mtz.tz(wallStr, tz).utcOffset(offBefore, true);
      const later = mtz.tz(wallStr, tz).utcOffset(offAfter, true);

      const startStr = startAmbigLocal.format('h:mm A');
      const endStr = displayEndLocal.format('h:mm A');

      return {
        kind: 'overlap',
        earlier,
        later,
        transitionLocal: transition.tz(tz).format('YYYY-MM-DD HH:mm'),
        message:
          `The selected time cannot be set accurately due to a Daylight Saving Time transition. ` +
          `Please choose a time outside the ${startStr} – ${endStr} window for this date, ` +
          `as this hour occurs twice when clocks are set back.`,
      };
    }
  }

  return { kind: 'ok', m: wall };
}
